/**
 * The Drift Exchange: buy and sell with everyone, on every world, from any bank.
 *
 * You post an offer ("buy 200 oak logs at 18 each", "sell 40 trout at 25"). What it costs is
 * taken from you the moment it's posted (coins for a buy, the items for a sell) and held by the
 * exchange, so nothing can be spent twice. Offers fill against each other on the server, as
 * often as they match, whoever is online and whatever world they're on. What you've bought, the
 * coins from what you've sold, and anything left when you cancel wait in the offer's box until
 * you collect them, into your bank, at any bank.
 *
 * A trade happens at the price of the offer that was there first (so a buyer who bid high gets
 * the difference back). The seller pays a small tax on each sale, which leaves the game.
 */
import { ITEMS, isItemId, type ItemId } from './items.js';

export const EX_SLOTS = 6;
/** The seller's share that goes to the exchange (taken off the coins, rounded down per offer). */
export const EX_TAX = 0.01;
/** Hours played before you can use it (like trading). */
export const EX_MIN_PLAY_HOURS = 2;
/** The most of one item in one offer, and the most coins one offer can be worth. */
export const EX_MAX_QTY = 1_000_000;
export const EX_MAX_VALUE = 2_000_000_000;
/** The guide price is the going rate over this many days (every trade, weighted by how many). */
export const EX_GUIDE_DAYS = 7;
export const EX_HISTORY_DAYS = 30;

export type ExSide = 'buy' | 'sell';
export interface ExOffer {
  id: number;
  side: ExSide;
  item: ItemId;
  /** Each, in coins. */
  price: number;
  qty: number;
  filled: number;
  /** Coins that have changed hands on it so far (the buyer paid, or the seller was paid, before tax). */
  spent: number;
  /** Tax taken so far (sells). */
  taxed: number;
  /** Waiting to be collected. */
  boxItems: number;
  boxCoins: number;
  /** Filled, or cancelled: nothing more will trade. */
  done: boolean;
  cancelled: boolean;
  at: number;
  /** Changed since (ms), for "your offer filled" news. */
  updated: number;
}
/** One day of trades in an item: the average price and how many changed hands. */
export interface ExDay { day: number; avg: number; n: number }
export interface ExGuide { item: ItemId; price: number; history: ExDay[]; /** Best offers on the book right now (none of yours). */ bestBuy: number | null; bestSell: number | null }

/** Things the exchange will take: anything from the item list but quest items and coins. */
export const exTradeable = (id: unknown): id is ItemId =>
  isItemId(id) && Object.prototype.hasOwnProperty.call(ITEMS, id) && ITEMS[id].cat !== 'quest' && !ITEMS[id].noted && id !== ('coins' as ItemId);

/** The tax on a sale worth `total`, rounded down. */
export const exTax = (total: number) => Math.floor(total * EX_TAX);

/**
 * Offers must sit near the going rate: no more than four times it, nor under a quarter of it.
 * It stops people passing coins to each other through junk sold at silly prices.
 */
export function exBand(guide: number): [number, number] {
  const g = Math.max(1, Math.round(guide));
  return [Math.max(1, Math.floor(g / 4)), Math.max(g * 4, g + 20)];
}
/** The price with nothing traded yet: what the item's worth to a trader. */
export const exBasePrice = (id: ItemId) => Math.max(1, ITEMS[id]?.value ?? 1);
